import { Businesses, Theme } from "@just-in-types";
import * as React from "react";
import styled from "react-emotion";
import Button from "./Button";
import Column from "./Column";
import Row from "./Row";

interface IKeypadProps {
  colors: Theme.Colors;
  disabled: boolean;
  onFocusNextInput?: () => void;
  onFocusPreviousInput?: () => void;
  onHandleKeypadClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isUppercase: boolean;
  site: Businesses.Site;
  type: "email" | "letters" | "numbers";
}

interface IKeypadState {
  uppercase: boolean;
}

const KeypadContainer = styled("div")`
  max-width: 100vw;
`;

const NumbersContainer = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`;

export const BackArrow = () => (
  <svg width="2rem" height="2rem" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
    <path d="M257.5 445.1l-22.2 22.2c-9.4 9.4-24.6 9.4-33.9 0L7 273c-9.4-9.4-9.4-24.6 0-33.9L201.4 44.7c9.4-9.4 24.6-9.4 33.9 0l22.2 22.2c9.5 9.5 9.3 25-.4 34.3L136.6 216H424c13.3 0 24 10.7 24 24v32c0 13.3-10.7 24-24 24H136.6l120.5 114.8c9.8 9.3 10 24.8.4 34.3z" />
  </svg>
);

class Keypad extends React.PureComponent<IKeypadProps, IKeypadState> {
  public static layouts = {
    email: [
      ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "backspace"],
      ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
      ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
      ["#", "z", "x", "c", "v", "b", "n", "m", "&"],
      ["!", "'", "+", "-", "_", ".", "@", ".com"],
    ],
    letters: [
      ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p", "backspace"],
      ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
      ["shift", "z", "x", "c", "v", "b", "n", "m"],
      ["previous", " ", "next"],
    ],
    numbers: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0"],
  };

  public state = { keys: <div />, uppercase: false };

  constructor(props: IKeypadProps) {
    super(props);

    const { isUppercase, type } = props;

    this.state = {
      keys: type === "email" || type === "letters" ? this.renderKeys(type) : this.renderNumbers(),
      uppercase: isUppercase,
    };
  }

  public componentDidUpdate({ isUppercase: prevIsUppercase }: IKeypadProps) {
    const { isUppercase } = this.props;

    if (prevIsUppercase !== isUppercase && isUppercase) {
      this.setState({ uppercase: isUppercase });
    }
  }

  public render() {
    const { type } = this.props;

    if (type === "email" || type === "letters") {
      return this.renderKeys(type);
    }

    return this.renderNumbers();
  }

  private renderKeys = (type: "email" | "letters") => {
    const {
      colors,
      disabled,
      isUppercase: uppercaseProp,
      onFocusNextInput,
      onFocusPreviousInput,
      onHandleKeypadClick,
      site: {
        kioskUiTextColor,
        kioskUiButtonPrimaryColor,
        kioskUiButtonErrorColor,
        kioskUiButtonSuccessColor,
        kioskUiButtonKeyColor,
      },
    } = this.props;
    const { uppercase: uppercaseState } = this.state;
    const uppercase = typeof uppercaseProp === "boolean" ? uppercaseProp : uppercaseState;
    const height = "8vmin";
    const width = "8vmin";

    const DotComButton = () => (
      <Button
        {...{ height, width: "12rem" }}
        bg={kioskUiTextColor ? kioskUiTextColor : colors.secondary}
        color="white"
        disabled={disabled}
        margin="0.5vmin"
        onClick={onHandleKeypadClick}
        value=".com"
      >
        .com
      </Button>
    );

    const BackspaceButton = () => (
      <Button
        {...{ height, width }}
        bg={kioskUiButtonErrorColor ? kioskUiButtonErrorColor : colors.error}
        color="white"
        disabled={disabled}
        margin="0.5vmin"
        onClick={onHandleKeypadClick}
        value="backspace"
      >
        <BackArrow />
      </Button>
    );

    const NextButton = () => (
      <Button
        {...{ height, width: "12rem" }}
        bg={kioskUiButtonSuccessColor ? kioskUiButtonSuccessColor : colors.success}
        color="white"
        disabled={disabled || !onFocusNextInput}
        margin="0.5vmin"
        onClick={onFocusNextInput}
        value="next"
      >
        ⇨
      </Button>
    );

    const PreviousButton = () => (
      <Button
        {...{ height, width: "12rem" }}
        bg={kioskUiButtonSuccessColor ? kioskUiButtonSuccessColor : colors.success}
        color="white"
        disabled={disabled || !onFocusPreviousInput}
        margin="0.5vmin"
        onClick={onFocusPreviousInput}
        value="previous"
      >
        ⇦
      </Button>
    );

    const ShiftButton = () => (
      <Button
        {...{ height, width }}
        bg={uppercase ? "white" : kioskUiButtonPrimaryColor ? kioskUiButtonPrimaryColor : colors.primary}
        color={uppercase ? (kioskUiButtonPrimaryColor ? kioskUiButtonPrimaryColor : colors.primary) : "white"}
        disabled={disabled}
        margin="0.5vmin"
        onClick={this.handleShift}
        value="shift"
      >
        ⇧
      </Button>
    );

    return (
      <KeypadContainer>
        <Column align="center">
          {Keypad.layouts[type].map((row, rowIndex) => (
            <Row key={rowIndex}>
              {row.map((value, keyIndex) => {
                switch (value) {
                  case ".com":
                    return <DotComButton key={keyIndex} />;
                  case "backspace":
                    return <BackspaceButton key={keyIndex} />;
                  case "next":
                    return <NextButton key={keyIndex} />;
                  case "previous":
                    return <PreviousButton key={keyIndex} />;
                  case "shift":
                    return <ShiftButton key={keyIndex} />;
                  default:
                    return (
                      <Button
                        bg={kioskUiButtonPrimaryColor ? kioskUiButtonPrimaryColor : colors.primary}
                        color={kioskUiButtonKeyColor ? kioskUiButtonKeyColor : "white"}
                        disabled={disabled}
                        height={height}
                        key={keyIndex}
                        margin="0.5vmin"
                        onClick={onHandleKeypadClick}
                        value={uppercase ? value.toUpperCase() : value}
                        width={value === " " ? "36vmin" : width}
                      >
                        {uppercase ? value.toUpperCase() : value}
                      </Button>
                    );
                }
              })}
            </Row>
          ))}
        </Column>
      </KeypadContainer>
    );
  };

  private renderNumbers = () => {
    const {
      colors,
      disabled,
      onHandleKeypadClick,
      site: { kioskUiButtonPrimaryColor, kioskUiButtonErrorColor, kioskUiButtonKeyColor },
    } = this.props;

    return (
      <NumbersContainer>
        {Keypad.layouts.numbers.map((value, index) => (
          <Button
            bg={
              value === "clear"
                ? kioskUiButtonErrorColor
                  ? kioskUiButtonErrorColor
                  : colors.error
                : kioskUiButtonPrimaryColor
                ? kioskUiButtonPrimaryColor
                : colors.primary
            }
            color={kioskUiButtonKeyColor ? kioskUiButtonKeyColor : "white"}
            disabled={disabled}
            key={index}
            margin="0.5vmin"
            onClick={onHandleKeypadClick}
            size="5vmin"
            value={value}
          >
            {value === "clear" ? "✖" : value}
          </Button>
        ))}
      </NumbersContainer>
    );
  };

  private handleShift = () => this.setState(({ uppercase }) => ({ uppercase: !uppercase }));
}

export default Keypad;



// WEBPACK FOOTER //
// ./src/components/Keypad.tsx